/**
 * TPRM — Third-Party Risk Management (Gestão de Fornecedores).
 * Route: /suppliers
 */
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Building2, Plus, AlertTriangle, ShieldCheck, Clock, Award, Loader2, Trash2, Sparkles, Edit3, ExternalLink, FileCheck2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { VerifyReviewsButton } from '../components/VerifyReviewsButton';
import { DocumentsLink } from '../components/DocumentsLink';

const CRITICALITY_META = {
  critical: { label: 'Crítico', tone: 'bg-red-50 text-red-700 border-red-200' },
  high: { label: 'Alto', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  medium: { label: 'Médio', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Baixo', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
const CATEGORY_LABEL = {
  cloud: 'Cloud/IaaS', saas: 'SaaS', consulting: 'Consultoria', hardware: 'Hardware', hosting: 'Hosting', connectivity: 'Conectividade', outsourcing: 'Outsourcing', other: 'Outros',
};
const DATA_ACCESS_OPTIONS = [
  { value: 'pii', label: 'PII (dados pessoais)' },
  { value: 'phi', label: 'PHI (saúde)' },
  { value: 'financial', label: 'Financeiros' },
  { value: 'credentials', label: 'Credenciais' },
  { value: 'source_code', label: 'Código fonte' },
  { value: 'none', label: 'Nenhum acesso a dados sensíveis' },
];

export default function Suppliers() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'ciso';
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [clausesTpl, setClausesTpl] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [filterCrit, setFilterCrit] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [certDialog, setCertDialog] = useState(null); // supplier
  const [certForm, setCertForm] = useState({ name: '', issuer: '', valid_from: '', valid_until: '', document_url: '' });
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewForm, setReviewForm] = useState({ outcome: 'approved', risk_score: 30, notes: '', next_review_months: 12 });

  const emptyForm = {
    name: '', category: 'saas', criticality: 'medium', contact_name: '', contact_email: '', contact_phone: '',
    website: '', data_access: [], services_provided: '', country: '', contract_clauses: {}, notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { document.title = 'Fornecedores · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params = filterCrit !== 'all' ? { criticality: filterCrit } : {};
        const [s, list, ar, tpl] = await Promise.all([
          api.get('/suppliers/stats'),
          api.get('/suppliers', { params }),
          api.get('/suppliers/at-risk'),
          api.get('/suppliers/clauses-template'),
        ]);
        if (ignore) return;
        setStats(s.data);
        setItems(list.data.items || []);
        setAtRisk(ar.data.items || []);
        setClausesTpl(tpl.data.clauses || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar fornecedores.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [refresh, filterCrit]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/suppliers/seed');
      toast.success(`${data.inserted} fornecedores demo criados.`);
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao seedar.');
    } finally { setSeeding(false); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (sup) => {
    setEditing(sup);
    setForm({
      name: sup.name, category: sup.category, criticality: sup.criticality,
      contact_name: sup.contact_name || '', contact_email: sup.contact_email || '', contact_phone: sup.contact_phone || '',
      website: sup.website || '', data_access: sup.data_access || [], services_provided: sup.services_provided || '',
      country: sup.country || '', contract_clauses: sup.contract_clauses || {}, notes: sup.notes || '',
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name || form.name.length < 2) { toast.error('Nome obrigatório.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/suppliers/${editing.supplier_id}`, form);
        toast.success('Fornecedor atualizado.');
      } else {
        await api.post('/suppliers', form);
        toast.success('Fornecedor criado.');
      }
      setShowForm(false);
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao guardar.');
    } finally { setSaving(false); }
  };

  const remove = async (sup) => {
    if (!confirm(`Remover ${sup.name}?`)) return;
    try {
      await api.delete(`/suppliers/${sup.supplier_id}`);
      toast.success('Fornecedor removido.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const addCert = async () => {
    if (!certForm.name || !certForm.valid_until) { toast.error('Nome e validade são obrigatórios.'); return; }
    try {
      await api.post(`/suppliers/${certDialog.supplier_id}/certifications`, certForm);
      toast.success('Certificação adicionada.');
      setCertDialog(null);
      setCertForm({ name: '', issuer: '', valid_from: '', valid_until: '', document_url: '' });
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const removeCert = async (sup, cert) => {
    try {
      await api.delete(`/suppliers/${sup.supplier_id}/certifications/${cert.cert_id}`);
      toast.success('Certificação removida.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error('Falha.'); }
  };

  const submitReview = async () => {
    try {
      await api.post(`/suppliers/${reviewDialog.supplier_id}/review`, reviewForm);
      toast.success('Reavaliação registada.');
      setReviewDialog(null);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  return (
    <Layout title="Fornecedores (TPRM)">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="suppliers-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-indigo-600" /> Fornecedores & Terceiros (TPRM)
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Cadeia de abastecimento crítica sob <strong>NIS2 Art. 21(2)(d)</strong> — due-diligence, certificações, cláusulas contratuais e reavaliação periódica.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && <VerifyReviewsButton />}
            {canEdit && stats?.total === 0 && (
              <Button variant="outline" onClick={seed} disabled={seeding} data-testid="suppliers-seed-btn">
                {seeding ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />} Popular demo
              </Button>
            )}
            {canEdit && (
              <Button onClick={openCreate} data-testid="suppliers-new-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Novo fornecedor
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { key: 'total', label: 'Total', value: stats?.total ?? 0, icon: Building2, tone: 'from-indigo-500 to-indigo-600' },
            { key: 'critical', label: 'Críticos', value: stats?.critical ?? 0, icon: AlertTriangle, tone: 'from-red-500 to-red-600' },
            { key: 'review_due', label: 'Revisões em atraso', value: stats?.review_due ?? 0, icon: Clock, tone: 'from-orange-500 to-orange-600' },
            { key: 'cert_expiring', label: 'Certs a expirar (60d)', value: stats?.cert_expiring_soon ?? 0, icon: Award, tone: 'from-amber-500 to-amber-600' },
            { key: 'cert_expired', label: 'Certs expiradas', value: stats?.cert_expired ?? 0, icon: ShieldCheck, tone: 'from-red-500 to-red-600' },
          ].map(k => (
            <Card key={k.key} data-testid={`suppliers-kpi-${k.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{k.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.tone} flex items-center justify-center`}><k.icon className="h-4 w-4 text-white" /></div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">Todos ({items.length})</TabsTrigger>
            <TabsTrigger value="at_risk" data-testid="tab-at-risk">Em risco ({atRisk.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            <div className="mb-3 flex items-center gap-2">
              <Select value={filterCrit} onValueChange={setFilterCrit}>
                <SelectTrigger className="w-48" data-testid="suppliers-filter-crit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as criticidades</SelectItem>
                  {Object.entries(CRITICALITY_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Card>
              <CardContent className="p-0">
                {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
                  items.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem fornecedores registados. Use &quot;Popular demo&quot; para começar.</div> :
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">Fornecedor</th>
                        <th className="text-left px-4 py-3">Categoria</th>
                        <th className="text-left px-4 py-3">Criticidade</th>
                        <th className="text-right px-4 py-3">Risk Score</th>
                        <th className="text-left px-4 py-3">Certificações</th>
                        <th className="text-left px-4 py-3">Próxima revisão</th>
                        <th className="text-right px-4 py-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map(s => {
                        const cm = CRITICALITY_META[s.criticality] || CRITICALITY_META.medium;
                        const rs = s.risk_score || 0;
                        return (
                          <tr key={s.supplier_id} className="hover:bg-slate-50" data-testid={`supplier-${s.supplier_id}`}>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{s.name}</div>
                              <div className="text-xs text-slate-500">{s.country || '—'} · {s.contact_email || '—'}</div>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{CATEGORY_LABEL[s.category] || s.category}</td>
                            <td className="px-4 py-3"><Badge variant="secondary" className={cm.tone}>{cm.label}</Badge></td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-bold tabular-nums ${rs >= 70 ? 'text-red-600' : rs >= 40 ? 'text-amber-600' : 'text-emerald-600'}`}>{rs}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(s.certifications || []).slice(0, 2).map(c => (
                                  <Badge key={c.cert_id} variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">{c.name}</Badge>
                                ))}
                                {(s.certifications || []).length === 0 && <span className="text-xs text-slate-400">Sem certs</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600">{s.next_review_date || '—'}</td>
                            <td className="px-4 py-3 text-right">
                              {canEdit && (
                                <div className="flex items-center gap-1 justify-end">
                                  <DocumentsLink module="supplier" entityId={s.supplier_id} title={s.name} />
                                  <Button variant="ghost" size="sm" onClick={() => setCertDialog(s)} data-testid={`cert-btn-${s.supplier_id}`} title="Adicionar certificação">
                                    <Award className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => setReviewDialog(s)} data-testid={`review-btn-${s.supplier_id}`} title="Reavaliação">
                                    <FileCheck2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-btn-${s.supplier_id}`}>
                                    <Edit3 className="h-3.5 w-3.5" />
                                  </Button>
                                  {user?.role === 'admin' && (
                                    <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`delete-btn-${s.supplier_id}`}>
                                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                }
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="at_risk">
            <Card>
              <CardContent className="p-0">
                {atRisk.length === 0 ? <div className="p-8 text-center text-emerald-600 text-sm">✓ Nenhum fornecedor em risco atualmente.</div> :
                  <div className="divide-y divide-slate-100">
                    {atRisk.map(r => (
                      <div key={r.supplier_id} className="p-4 hover:bg-slate-50" data-testid={`at-risk-${r.supplier_id}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${r.risk_score >= 70 ? 'bg-red-100' : 'bg-amber-100'}`}>
                            <AlertTriangle className={`h-5 w-5 ${r.risk_score >= 70 ? 'text-red-600' : 'text-amber-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900">{r.name}</h3>
                              <Badge variant="secondary" className={CRITICALITY_META[r.criticality]?.tone}>{CRITICALITY_META[r.criticality]?.label}</Badge>
                              <span className={`text-sm font-bold ${r.risk_score >= 70 ? 'text-red-600' : 'text-amber-600'}`}>Score: {r.risk_score}</span>
                            </div>
                            <ul className="text-sm text-slate-700 space-y-0.5">
                              {r.reasons.map((rr, i) => <li key={i}>• {rr}</li>)}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                }
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="supplier-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar: ${editing.name}` : 'Novo fornecedor'}</DialogTitle>
            <DialogDescription>Ficha completa com dados de contacto, criticidade, categorias de dados e cláusulas contratuais obrigatórias.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></div>
              <div><Label>Website</Label><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} data-testid="form-website" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Criticidade</Label>
                <Select value={form.criticality} onValueChange={v => setForm({ ...form, criticality: v })}>
                  <SelectTrigger data-testid="form-criticality"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CRITICALITY_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>País</Label><Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="form-country" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Nome contacto</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div><Label>Email contacto</Label><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div><Label>Telefone</Label><Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} /></div>
            </div>
            <div>
              <Label>Serviços prestados</Label>
              <Textarea rows={2} value={form.services_provided} onChange={e => setForm({ ...form, services_provided: e.target.value })} />
            </div>
            <div>
              <Label>Categorias de dados a que tem acesso</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {DATA_ACCESS_OPTIONS.map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.data_access.includes(opt.value)} onChange={e => {
                      const next = e.target.checked ? [...form.data_access, opt.value] : form.data_access.filter(x => x !== opt.value);
                      setForm({ ...form, data_access: next });
                    }} data-testid={`form-data-${opt.value}`} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Cláusulas contratuais presentes</Label>
              <div className="space-y-2 mt-2 border border-slate-200 rounded p-3 max-h-52 overflow-y-auto">
                {clausesTpl.map(c => (
                  <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`clause-${c.key}`}>
                    <input
                      type="checkbox"
                      checked={!!form.contract_clauses[c.key]}
                      onChange={e => setForm({ ...form, contract_clauses: { ...form.contract_clauses, [c.key]: e.target.checked } })}
                    />
                    <span className={c.required ? 'font-medium' : 'text-slate-600'}>
                      {c.label}
                      {c.required && <Badge variant="secondary" className="ml-2 bg-red-50 text-red-700 border-red-200 text-[10px]">Obrigatória</Badge>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Notas</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="form-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : editing ? 'Atualizar' : 'Criar fornecedor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add cert dialog */}
      <Dialog open={!!certDialog} onOpenChange={(o) => { if (!o) setCertDialog(null); }}>
        <DialogContent data-testid="cert-dialog">
          <DialogHeader>
            <DialogTitle>Adicionar certificação · {certDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={certForm.name} onChange={e => setCertForm({ ...certForm, name: e.target.value })} placeholder="ISO 27001, SOC 2 Type II, PCI DSS…" data-testid="cert-name" /></div>
            <div><Label>Emissor</Label><Input value={certForm.issuer} onChange={e => setCertForm({ ...certForm, issuer: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Válido desde</Label><Input type="date" value={certForm.valid_from} onChange={e => setCertForm({ ...certForm, valid_from: e.target.value })} /></div>
              <div><Label>Válido até *</Label><Input type="date" value={certForm.valid_until} onChange={e => setCertForm({ ...certForm, valid_until: e.target.value })} data-testid="cert-valid-until" /></div>
            </div>
            <div><Label>URL do documento</Label><Input value={certForm.document_url} onChange={e => setCertForm({ ...certForm, document_url: e.target.value })} /></div>
          </div>
          {(certDialog?.certifications || []).length > 0 && (
            <div className="border-t border-slate-200 pt-3 mt-3">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Certs existentes</h4>
              <div className="space-y-1">
                {certDialog.certifications.map(c => (
                  <div key={c.cert_id} className="flex items-center justify-between text-sm">
                    <span>{c.name} <span className="text-slate-400">· expira {c.valid_until}</span></span>
                    <Button size="sm" variant="ghost" onClick={() => removeCert(certDialog, c)}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCertDialog(null)}>Fechar</Button>
            <Button onClick={addCert} data-testid="cert-submit">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={(o) => { if (!o) setReviewDialog(null); }}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Reavaliação · {reviewDialog?.name}</DialogTitle>
            <DialogDescription>Regista uma reavaliação periódica. Actualiza a próxima data de revisão.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Resultado</Label>
              <Select value={reviewForm.outcome} onValueChange={v => setReviewForm({ ...reviewForm, outcome: v })}>
                <SelectTrigger data-testid="review-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Aprovado</SelectItem>
                  <SelectItem value="conditional">Aprovado com condições</SelectItem>
                  <SelectItem value="rejected">Rejeitado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Risk score (0-100)</Label><Input type="number" min={0} max={100} value={reviewForm.risk_score} onChange={e => setReviewForm({ ...reviewForm, risk_score: Number(e.target.value) })} data-testid="review-score" /></div>
            <div><Label>Próxima revisão (meses)</Label><Input type="number" min={1} max={48} value={reviewForm.next_review_months} onChange={e => setReviewForm({ ...reviewForm, next_review_months: Number(e.target.value) })} /></div>
            <div><Label>Notas</Label><Textarea rows={3} value={reviewForm.notes} onChange={e => setReviewForm({ ...reviewForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewDialog(null)}>Cancelar</Button>
            <Button onClick={submitReview} data-testid="review-submit">Registar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
